import { Link } from "react-router";
import { AlertCircle, Bell, CheckCircle2, Clock } from "lucide-react";
import {
  Body,
  Caption,
  CardTitle,
  cn,
  VButton,
  VDropdownMenu,
  VDropdownMenuContent,
  VDropdownMenuSeparator,
  VDropdownMenuTrigger,
} from "@/components/ui";
import { RoleSwitcher } from "@/domains/auth";

type NotificationKind = "info" | "success" | "warning";

type HeaderNotification = {
  id: string;
  kind: NotificationKind;
  title: string;
  description: string;
  timeAgo: string;
  to: string;
};

const MOCK_NOTIFICATIONS: ReadonlyArray<HeaderNotification> = [
  {
    id: "n1",
    kind: "info",
    title: "Nieuwe aanvraag · AANV-2026-9231",
    description: "Maarten Hendrickx vraagt camera op Theaterplein voor markten.",
    timeAgo: "5 min",
    to: "/requests/req-9231",
  },
  {
    id: "n2",
    kind: "warning",
    title: "Onderhoud vereist · MC-008",
    description: "Mobiele camera meldt batterij onder 15%.",
    timeAgo: "1 u",
    to: "/assets/ast-c-008",
  },
  {
    id: "n3",
    kind: "success",
    title: "Dossier afgesloten · #9199",
    description: "Park Spoor Noord — plaatsingstaken afgerond, dossier gearchiveerd.",
    timeAgo: "3 u",
    to: "/cases/case-9199",
  },
];

const KIND_ICON: Record<NotificationKind, React.ComponentType<{ className?: string }>> = {
  info: AlertCircle,
  success: CheckCircle2,
  warning: Clock,
};

const KIND_TONE: Record<NotificationKind, string> = {
  info: "text-info-600",
  success: "text-success-600",
  warning: "text-warning-600",
};

export function AppHeader(): React.JSX.Element {
  const count = MOCK_NOTIFICATIONS.length;

  return (
    <header className="bg-gradient-to-r from-primary-700 to-primary-600 text-on-primary">
      <div className="flex items-center gap-4 px-6 py-2">
        <div className="flex h-14 w-[60px] items-center justify-center overflow-hidden rounded-sm shadow-md">
          <img
            src="/politie-wordmark.jpg"
            alt="Politiezone Antwerpen"
            className="block h-[68px] w-auto max-w-none"
          />
        </div>

        <div className="flex-1 leading-tight">
          <CardTitle className="text-lg font-bold text-on-primary">
            Camerabeheer
          </CardTitle>
          <p className="text-xs text-primary-100">Politiezone Antwerpen</p>
        </div>

        <VDropdownMenu>
          <VDropdownMenuTrigger asChild>
            <VButton
              variant="ghost"
              size="icon"
              className="relative text-on-primary hover:bg-primary-500/40"
              aria-label={`Notificaties${count ? ` (${count} ongelezen)` : ""}`}
              icon={
                <>
                  <Bell className="size-5" aria-hidden />
                  {count > 0 ? (
                    <span className="absolute right-1 top-1 inline-flex size-4 items-center justify-center rounded-full bg-error-600 text-[10px] font-bold text-white">
                      {count}
                    </span>
                  ) : null}
                </>
              }
            />
          </VDropdownMenuTrigger>
          <VDropdownMenuContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between px-3 pb-2 pt-3">
              <Body weight="semibold">Notificaties</Body>
              <Caption tone="muted">{count} nieuw</Caption>
            </div>
            <VDropdownMenuSeparator className="my-0" />
            {count === 0 ? (
              <div className="px-3 py-6 text-center">
                <Caption tone="muted">Geen meldingen</Caption>
              </div>
            ) : (
              <ul className="max-h-80 overflow-y-auto">
                {MOCK_NOTIFICATIONS.map((n) => {
                  const Icon = KIND_ICON[n.kind];
                  return (
                    <li key={n.id} className="border-b border-border last:border-b-0">
                      <Link
                        to={n.to}
                        className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-muted"
                      >
                        <Icon
                          className={cn("mt-0.5 size-4 shrink-0", KIND_TONE[n.kind])}
                          aria-hidden
                        />
                        <div className="flex-1 space-y-0.5">
                          <Body weight="medium" className="leading-snug">
                            {n.title}
                          </Body>
                          <Caption tone="muted" as="p" className="line-clamp-2">
                            {n.description}
                          </Caption>
                          <Caption tone="muted" className="text-[11px]">
                            {n.timeAgo} geleden
                          </Caption>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            <VDropdownMenuSeparator className="my-0" />
            <div className="p-2">
              <VButton variant="ghost" size="sm" className="w-full justify-center">
                Alle meldingen bekijken
              </VButton>
            </div>
          </VDropdownMenuContent>
        </VDropdownMenu>

        <RoleSwitcher />
      </div>
    </header>
  );
}
